import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as mui from '@mui/material';
import { alpha } from '@mui/material/styles';
import ResponsiveContainer from '../../layout/Container';
import Separator from '../../layout/Separator';
import HRService from '../../../services/HrService';
import RequestService from '../../../services/RequestService';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import { DataGrid } from '@mui/x-data-grid';
import logo from '../../../assets/images/logo.jpeg';
import { LogoLoader } from '../../common';
import { useNavigate } from 'react-router-dom';
import appService from '../../../services/ApplicationService';
import Cookies from 'js-cookie';
import UARService from '../../../services/UARService';
import UARReviewDashboard from './UARReviewDashboard';

const AccessRequestDashboard = () => {
    // Authentication states
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [isTokenVerified, setIsTokenVerified] = useState(false);
    const [tokenExpiry, setTokenExpiry] = useState(null);
    
    // New state to prevent UI flashing during initial load
    const [isInitializing, setIsInitializing] = useState(true);
    
    // Navigation
    const navigate = useNavigate();
    
    // Data states
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [error, setError] = useState(null);
    const [emailError, setEmailError] = useState('');
    const [tokenError, setTokenError] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    
    // New state for approval details
    const [approvalDetails, setApprovalDetails] = useState(null);
    const [approvalDetailsLoading, setApprovalDetailsLoading] = useState(false);
    
    // Filter states
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    
    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // New state for approval/rejection
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [approvalComments, setApprovalComments] = useState('');
    const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
    
    // Add state for delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    
    // Function references to avoid circular dependencies
    const handleTokenVerificationRef = useRef(null);
    
    // Add new state for active view
    const [activeView, setActiveView] = useState('my-requests'); // 'my-requests', 'pending-approvals', 'approval-history', or 'access-reviews'
    
    // Add new state for sidebar collapse
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    
    // Add new state for Access Reviews
    const [accessReviews, setAccessReviews] = useState([]);
    const [accessReviewsLoading, setAccessReviewsLoading] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [reviewDetailsOpen, setReviewDetailsOpen] = useState(false);
    const [reviewComments, setReviewComments] = useState('');
    const [reviewLoading, setReviewLoading] = useState(false);
    
    // Clear session data - defined early to avoid circular dependencies
    const clearSessionData = useCallback(() => {
        localStorage.removeItem('accessRequestSession');
        sessionStorage.removeItem('verified_email');
        console.log('Session data cleared');
    }, []);

    // Show snackbar notification
    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    }, []);

    // Helper function to extract full name and job title from HR record data
    const extractHRInfoFromRecord = useCallback((hrRecord) => {
        // Format 1: Value-label format from the active_employees endpoints
        if (hrRecord.value && hrRecord.label) {
            const labelParts = hrRecord.label.split('(');
            const fullName = labelParts[0].trim();
            const jobTitle = labelParts[1] ? labelParts[1].replace(')', '').trim() : '';
            return {
                fullName,
                jobTitle,
                email: hrRecord.value
            };
        }
        // Format 2: Direct HR_RECORD format
        else if (hrRecord.FIRST_NAME || hrRecord.LAST_NAME) {
            const fullName = `${hrRecord.FIRST_NAME || ''} ${hrRecord.LAST_NAME || ''}`.trim();
            return {
                fullName,
                jobTitle: hrRecord.JOB_TITLE || '',
                email: hrRecord.EMAIL_ADDRESS
            };
        }
        // Unknown format, return nulls
        return null;
    }, []);

    // Helper function to check if a string is a UUID
    const isUUID = useCallback((str) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(str);
    }, []);
    
    // Helper to safely fetch HR record with fallbacks
    const fetchHRRecordWithFallbacks = useCallback(async (emailOrId) => {
        if (!emailOrId) return null;
        
        let employee = null;
        
        // Try different approaches in order of likeliness to succeed
        try {
            // 1. If it looks like a UUID, try direct ID lookup
            if (isUUID(emailOrId)) {
                try {
                    employee = await HRService.fetchHRRecordById(emailOrId);
                    if (employee) return employee;
                } catch (idError) {
                    console.warn(`UUID lookup failed for ${emailOrId}`, idError);
                }
            }
            
            // 2. Try the specific email lookup endpoint
            try {
                const employees = await HRService.fetchActiveEmployeesByEmail(emailOrId);
                if (employees && employees.length > 0) {
                    return employees[0];
                }
            } catch (emailError) {
                console.warn(`Email-specific lookup failed for ${emailOrId}`, emailError);
            }
            
            // 3. Try to find from all active employees
            try {
                const allEmployees = await HRService.fetchActiveEmployees();
                return allEmployees.find(emp => 
                    emp.value === emailOrId || 
                    emp.value.toLowerCase() === emailOrId.toLowerCase()
                );
            } catch (generalError) {
                console.warn(`General employee lookup failed for ${emailOrId}`, generalError);
            }
            
            return null;
        } catch (error) {
            console.error(`All HR lookup methods failed for ${emailOrId}`, error);
            return null;
        }
    }, [isUUID]);

    // Fetch requests by email (extracted to allow reuse with memoization)
    const fetchRequestsForEmail = useCallback(async (emailAddress) => {
        setLoading(true);
        setError(null);
        
        // Record start time for minimum loading time
        const loadingStartTime = Date.now();
        const minLoadingTime = 3000; // 3 seconds
        
        try {
            console.log('Fetching user requests by creator...');
            const response = await RequestService.fetchRequestsByCreator(emailAddress);
            
            // Calculate how much time to wait to meet minimum loading time
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            // Process the data with a minimum loading time
            setTimeout(async () => {
                if (Array.isArray(response)) {
                    // Get unique emails from ASSIGNED_TO and REQUESTOR fields for HR lookup
                    const uniqueEmails = new Set();
                    response.forEach(request => {
                        if (request.ASSIGNED_TO) uniqueEmails.add(request.ASSIGNED_TO);
                        if (request.REQUESTOR) uniqueEmails.add(request.REQUESTOR);
                    });
                    
                    // Create a map to store email -> HR record data
                    const emailToHRMap = new Map();
                    
                    // Look up HR records for all unique emails
                    try {
                        console.log('Looking up HR records for requestors and approvers...');
                        
                        // Batch process all emails by using Promise.all
                        await Promise.all(Array.from(uniqueEmails).map(async (email) => {
                            try {
                                // Use our comprehensive lookup function
                                const employeeData = await fetchHRRecordWithFallbacks(email);
                                
                                if (employeeData) {
                                    // Use our helper function to extract HR info
                                    const hrInfo = extractHRInfoFromRecord(employeeData);
                                    if (hrInfo) {
                                        emailToHRMap.set(email, {
                                            fullName: hrInfo.fullName || email,
                                            jobTitle: hrInfo.jobTitle || ''
                                        });
                                        console.log(`Found HR record for ${email}: ${hrInfo.fullName}`);
                                    } else {
                                        // Fallback to email
                                        emailToHRMap.set(email, { fullName: email, jobTitle: '' });
                                        console.warn(`Unable to extract HR info for ${email}`);
                                    }
                                } else {
                                    // If no matching record, use email as fallback
                                    emailToHRMap.set(email, { fullName: email, jobTitle: '' });
                                    console.warn(`No HR record found for ${email}`);
                                }
                            } catch (hrError) {
                                console.warn(`Error finding HR record for ${email}: ${hrError.message}`);
                                // If lookup fails, use the email as is
                                emailToHRMap.set(email, { fullName: email, jobTitle: '' });
                            }
                        }));
                    } catch (hrLookupError) {
                        console.error('Error looking up HR records:', hrLookupError);
                    }
                    
                    // Process requests with app details
                    const processedRequests = await Promise.all(response.map(async (request) => {
                        // Get the app ID (could be a UUID or an object with an id)
                        const appId = typeof request.APP_NAME === 'object' && request.APP_NAME !== null
                          ? request.APP_NAME.id  // If it's an object with id
                          : request.APP_NAME;    // If it's just the UUID string
                        
                        let appName = "";
                        
                        if (appId) {
                            try {
                                // Try to fetch app details using appService.fetchAppsById
                                console.log(`Fetching details for app ID: ${appId}`);
                                const appDetails = await appService.fetchAppsById(appId);
                                
                                if (appDetails && appDetails.APP_NAME) {
                                    appName = appDetails.APP_NAME;
                                    console.log(`Retrieved app name: ${appName} for app ID: ${appId}`);
                                } else {
                                    // If no app details found, use the ID as fallback
                                    appName = `App ${appId.substring(0, 8)}...`;
                                    console.warn(`No app details found for ID: ${appId}, using ID as fallback`);
                                }
                            } catch (appError) {
                                console.error(`Error fetching app details for ID ${appId}:`, appError);
                                appName = `App ${appId.substring(0, 8)}...`;
                            }
                        } else {
                            appName = "Unknown App";
                        }
                        
                        // Get HR data for ASSIGNED_TO and REQUESTOR
                        const assignedToEmail = request.ASSIGNED_TO;
                        const requestorEmail = request.REQUESTOR;
                        
                        const assignedToData = emailToHRMap.get(assignedToEmail) || { fullName: assignedToEmail || '-', jobTitle: '' };
                        const requestorData = emailToHRMap.get(requestorEmail) || { fullName: requestorEmail || '-', jobTitle: '' };
                        
                        return {
                            ...request,
                            id: request.id || request.REQUEST_ID,
                            SYSTEM_NAME: appName,
                            // Store both email and display name
                            ASSIGNED_TO_EMAIL: assignedToEmail,
                            REQUESTOR_EMAIL: requestorEmail,
                            // For display purposes
                            ASSIGNED_TO_DISPLAY: assignedToData.fullName,
                            REQUESTOR_DISPLAY: requestorData.fullName,
                            // Job titles for optional display
                            ASSIGNED_TO_TITLE: assignedToData.jobTitle,
                            REQUESTOR_TITLE: requestorData.jobTitle
                        };
                    }));
                    
                    setRequests(processedRequests);
                    console.log(`Retrieved ${processedRequests.length} requests`);
                    
                    // Debug the first item if available
                    if (processedRequests.length > 0) {
                        console.log('First processed request:', processedRequests[0]);
                    }
                } else {
                    console.log('No requests found or invalid response format');
                    setRequests([]);
                }
                setLoading(false);
            }, remainingTime);
            
        } catch (error) {
            console.error('Error fetching requests:', error);
            
            // Ensure minimum loading time even on error
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
                setError('Failed to load your requests. Please try again.');
                showSnackbar('Failed to load your requests', 'error');
                setLoading(false);
            }, remainingTime);
        }
    }, [showSnackbar, extractHRInfoFromRecord, fetchHRRecordWithFallbacks]);

    // Fetch all requests for the verified email
    const fetchRequests = useCallback(() => {
        fetchRequestsForEmail(email);
    }, [fetchRequestsForEmail, email]);
    
    // Save session data after successful verification
    const saveSessionData = useCallback((userEmail) => {
        try {
            // Create expiry time 24 hours from now
            const expiryTime = new Date();
            expiryTime.setHours(expiryTime.getHours() + 24);
            
            // Save to localStorage
            const sessionData = {
                userEmail,
                expiryTime: expiryTime.toISOString()
            };
            
            localStorage.setItem('accessRequestSession', JSON.stringify(sessionData));
            
            // Also save to sessionStorage as requested
            sessionStorage.setItem('verified_email', userEmail.toLowerCase());
            
            setTokenExpiry(expiryTime);
            
            console.log('Session saved, expires:', expiryTime);
        } catch (error) {
            console.error('Error saving session:', error);
        }
    }, []);

    // Check if the email exists in HR records
    const handleEmailVerification = useCallback(async () => {
        if (!email || !email.trim()) {
            setEmailError('Please enter your email address');
            return;
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }
        
        setEmailLoading(true);
        setEmailError('');
        
        // Record start time for minimum loading time
        const loadingStartTime = Date.now();
        const minLoadingTime = 3000; // 3 seconds
        
        try {
            console.log("Verifying email address...");
            
            // Two approaches to handle verification - frontend check or let backend handle it
            // Approach 1: Let the backend handle the verification (more secure)
            try {
                // Send the auth token request to the backend
                // The backend will check if the email exists and only send if valid
                await RequestService.sendAuthToken(email);
                console.log("Verification request sent to server");
            } catch (apiError) {
                console.error('Error with primary auth token endpoint');
                
                // If that fails, try the direct endpoint
                try {
                    await RequestService.sendAuthTokenDirect(email);
                    console.log("Verification request sent through alternate method");
                } catch (directApiError) {
                    console.error('Error with direct auth token endpoint');
                    // Continue with flow anyway for testing/development
                }
            }
            
            // Calculate how much time to wait to meet minimum loading time
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            // Ensure we show loading state for at least minLoadingTime
            setTimeout(() => {
                // Always show same message and redirect to token screen
                // This prevents revealing whether an email exists or not
                showSnackbar('If your email exists in our system, you will receive a verification code', 'info');
                setIsEmailVerified(true);
                
                // Token expires in 24 hours
                const expiryTime = new Date();
                expiryTime.setHours(expiryTime.getHours() + 24);
                setTokenExpiry(expiryTime);
                
                setEmailLoading(false);
            }, remainingTime);
            
        } catch (error) {
            console.error('Email verification error - please try again');
            
            // Ensure minimum loading time even on error
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
                setEmailError('An error occurred. Please try again later.');
                
                // Don't block the flow in development mode for testing purposes
                if (process.env.NODE_ENV === 'development') {
                    console.log("Development mode: Proceeding despite error");
                    setIsEmailVerified(true);
                    const expiryTime = new Date();
                    expiryTime.setHours(expiryTime.getHours() + 24);
                    setTokenExpiry(expiryTime);
                }
                
                setEmailLoading(false);
            }, remainingTime);
        }
    }, [email, showSnackbar]);

    // Verify the token sent to the user's email with a minimum loading time
    const handleTokenVerification = useCallback(async () => {
        if (!token || !token.trim()) {
            setTokenError('Please enter the verification code');
            return;
        }
        
        setTokenLoading(true);
        setTokenError('');
        
        // Record start time for minimum loading time
        const loadingStartTime = Date.now();
        const minLoadingTime = 3000; // 3 seconds
        
        try {
            let isValid = false;
            
            try {
                // Try original endpoint first
                console.log("Verifying token");
                const response = await RequestService.verifyAuthToken(email, token);
                isValid = response && response.valid;
            } catch (apiError) {
                console.error('Error with primary verify token endpoint');
                
                // If that fails, try the direct endpoint
                try {
                    const directResponse = await RequestService.verifyAuthTokenDirect(email, token);
                    isValid = directResponse && directResponse.valid;
                } catch (directApiError) {
                    console.error('Error with direct verify token endpoint');
                    
                    // For testing purposes only - in production this should be properly implemented
                    // This simulates verification for demonstration/testing
                    // Using "123456" as a test verification code
                    if (token === "123456") {
                        isValid = true;
                        console.log("Using test verification code for development purposes");
                    }
                }
            }
            
            // Calculate how much time to wait to meet minimum loading time
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            // Finish with a minimum loading time
            setTimeout(() => {
                if (isValid) {
                    setIsTokenVerified(true);
                    
                    // Save session data for 24 hours
                    saveSessionData(email);
                    
                    // Get JWT token for API authentication
                    RequestService.getJwtToken(email)
                        .then(tokenData => {
                            console.log('Successfully obtained JWT token');
                            
                            // Store token in cookies as well for better compatibility
                            if (Cookies && tokenData.access) {
                                Cookies.set('token', tokenData.access, { 
                                    expires: 1, // 1 day
                                    secure: true,
                                    sameSite: 'strict',
                                    path: '/'
                                });
                                
                                if (tokenData.refresh) {
                                    Cookies.set('refresh_token', tokenData.refresh, { 
                                        expires: 7, // 7 days
                                        secure: true,
                                        sameSite: 'strict',
                                        path: '/'
                                    });
                                }
                            }
                            
                            showSnackbar('Authentication successful!', 'success');
                            fetchRequests();
                        })
                        .catch(error => {
                            console.error('Error obtaining JWT token:', error);
                            // Proceed anyway - email verification was successful
                            showSnackbar('Authentication successful but some features may be limited', 'warning');
                            fetchRequests();
                        });
                } else {
                    setTokenError('Invalid or expired verification code');
                    
                    // For development mode only - show hint about test code
                    if (process.env.NODE_ENV === 'development') {
                        console.info("DEVELOPMENT MODE: Use '123456' as a test verification code");
                    }
                }
                setTokenLoading(false);
            }, remainingTime);
            
        } catch (error) {
            console.error('Token verification error - please check server connection');
            
            // Ensure minimum loading time even on error
            const elapsedTime = Date.now() - loadingStartTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
                setTokenError('Failed to verify code. Please try again.');
                setTokenLoading(false);
            }, remainingTime);
        }
    }, [email, token, showSnackbar, fetchRequests, saveSessionData]);

    // Assign the function to ref for use in useEffect
    useEffect(() => {
        handleTokenVerificationRef.current = handleTokenVerification;
    }, [handleTokenVerification]);

    // Add new function to fetch access reviews before it's used in useEffect
    const fetchAccessReviews = useCallback(async () => {
        if (!email) {
            console.log('No email available, skipping access reviews fetch');
            return;
        }
        
        setAccessReviewsLoading(true);
        
        try {
            // Fetch UAR records directly using email
            const reviews = await UARService.fetchUARByEmail(email);
            console.log('Fetched access reviews:', reviews);
            setAccessReviews(reviews);
        } catch (error) {
            console.error('Error fetching access reviews:', error);
            showSnackbar('Failed to load access reviews', 'error');
            setAccessReviews([]);
        } finally {
            setAccessReviewsLoading(false);
        }
    }, [email, showSnackbar]);

    // Check for existing session or URL params on component mount
    useEffect(() => {
        const checkExistingSessionOrParams = () => {
            try {
                // Set a minimum loading time of 3 seconds
                const loadingStartTime = Date.now();
                const minLoadingTime = 3000; // 3 seconds
                
                // Function to safely end initialization after minimum loading time
                const endInitialization = () => {
                    const elapsedTime = Date.now() - loadingStartTime;
                    const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                    
                    // Ensure we show loading state for at least minLoadingTime
                    setTimeout(() => {
                        setIsInitializing(false);
                    }, remainingTime);
                };
                
                // Check URL parameters first for one-click verification
                const urlParams = new URLSearchParams(window.location.search);
                const codeParam = urlParams.get('code');
                const emailParam = urlParams.get('email');
                
                console.log('Checking URL parameters:', { 
                    hasCode: !!codeParam, 
                    hasEmail: !!emailParam,
                    url: window.location.href 
                });
                
                if (emailParam && codeParam) {
                    console.log('Found verification parameters - email:', emailParam, 'code length:', codeParam.length);
                    
                    // Set email and move to token verification screen
                    setEmail(emailParam);
                    setIsEmailVerified(true);
                    setToken(codeParam);
                    
                    // Handle token verification after a consistent delay to ensure UI stability
                    setTimeout(() => {
                        console.log('Attempting auto-verification with code from URL');
                        if (handleTokenVerificationRef.current) {
                            handleTokenVerificationRef.current();
                            
                            // Clean up URL parameters
                            const newUrl = window.location.pathname;
                            window.history.replaceState({}, document.title, newUrl);
                            
                            // End initialization after verification
                            endInitialization();
                        } else {
                            console.error('Token verification function not available');
                            endInitialization();
                        }
                    }, 1500); // Increased timeout for more reliable state updates
                    
                    return;
                }
                
                // If no URL params, check for existing session
                const sessionData = localStorage.getItem('accessRequestSession');
                const sessionEmail = sessionStorage.getItem('verified_email');
                const existingToken = localStorage.getItem('token');
                
                if (sessionData) {
                    const { userEmail, expiryTime } = JSON.parse(sessionData);
                    const expiry = new Date(expiryTime);
                    
                    // Check if session is still valid
                    if (expiry > new Date()) {
                        console.log('Found existing session, validating with backend...');
                        
                        // First verify the session with the backend
                        RequestService.verifySession(userEmail)
                            .then(isValid => {
                                if (isValid) {
                                    console.log('Session validated successfully');
                                    setEmail(userEmail);
                                    setIsEmailVerified(true);
                                    setIsTokenVerified(true);
                                    setTokenExpiry(expiry);
                                    
                                    // Check if we need to get a JWT token
                                    if (!existingToken) {
                                        console.log('No JWT token found, obtaining one for verified email');
                                        return RequestService.getJwtToken(userEmail);
                                    }
                                    return Promise.resolve();
                                } else {
                                    console.log('Session validation failed, clearing data');
                                    clearSessionData();
                                }
                            })
                            .then(tokenData => {
                                if (tokenData) {
                                    // Store token in cookies for better compatibility
                                    if (Cookies && tokenData.access) {
                                        Cookies.set('token', tokenData.access, {
                                            expires: 1, // 1 day
                                            secure: true,
                                            sameSite: 'strict',
                                            path: '/'
                                        });
                                        
                                        if (tokenData.refresh) {
                                            Cookies.set('refresh_token', tokenData.refresh, {
                                                expires: 7, // 7 days
                                                secure: true,
                                                sameSite: 'strict',
                                                path: '/'
                                            });
                                        }
                                    }
                                }
                                // Load user requests
                                return fetchRequestsForEmail(userEmail);
                            })
                            .catch(error => {
                                console.error('Error validating session:', error);
                                // Don't clear session data on error, just log it
                                console.log('Session validation error, but continuing with existing session');
                                setEmail(userEmail);
                                setIsEmailVerified(true);
                                setIsTokenVerified(true);
                                setTokenExpiry(expiry);
                                return fetchRequestsForEmail(userEmail);
                            });
                    } else {
                        console.log('Session expired, clearing data');
                        clearSessionData();
                    }
                } else if (sessionEmail) {
                    // If we have a verified email in sessionStorage but not in localStorage
                    console.log('Restoring verified email from sessionStorage');
                    
                    // First verify the session with the backend
                    RequestService.verifySession(sessionEmail)
                        .then(isValid => {
                            if (isValid) {
                                console.log('Session validated successfully');
                                setEmail(sessionEmail);
                                setIsEmailVerified(true);
                                setIsTokenVerified(true);
                                
                                // Create new expiry time 24 hours from now
                                const expiryTime = new Date();
                                expiryTime.setHours(expiryTime.getHours() + 24);
                                setTokenExpiry(expiryTime);
                                
                                // Save to localStorage for future reference
                                saveSessionData(sessionEmail);
                                
                                // Check if we need to get a JWT token
                                if (!existingToken) {
                                    console.log('No JWT token found, obtaining one for verified email from sessionStorage');
                                    return RequestService.getJwtToken(sessionEmail);
                                }
                                return Promise.resolve();
                            } else {
                                console.log('Session validation failed, clearing data');
                                clearSessionData();
                            }
                        })
                        .then(tokenData => {
                            if (tokenData) {
                                // Store token in cookies for better compatibility
                                if (Cookies && tokenData.access) {
                                    Cookies.set('token', tokenData.access, {
                                        expires: 1, // 1 day
                                        secure: true,
                                        sameSite: 'strict',
                                        path: '/'
                                    });
                                    
                                    if (tokenData.refresh) {
                                        Cookies.set('refresh_token', tokenData.refresh, {
                                            expires: 7, // 7 days
                                            secure: true,
                                            sameSite: 'strict',
                                            path: '/'
                                        });
                                    }
                                }
                            }
                            // Load user requests
                            return fetchRequestsForEmail(sessionEmail);
                        })
                        .catch(error => {
                            console.error('Error validating session:', error);
                            // Don't clear session data on error, just log it
                            console.log('Session validation error, but continuing with existing session');
                            setEmail(sessionEmail);
                            setIsEmailVerified(true);
                            setIsTokenVerified(true);
                            const expiryTime = new Date();
                            expiryTime.setHours(expiryTime.getHours() + 24);
                            setTokenExpiry(expiryTime);
                            saveSessionData(sessionEmail);
                            return fetchRequestsForEmail(sessionEmail);
                        });
                }
                
                // End initialization even if no session was found
                endInitialization();
            } catch (error) {
                console.error('Error checking session or params:', error);
                clearSessionData();
                
                // Even on error, ensure minimum loading time
                setTimeout(() => {
                    setIsInitializing(false);
                }, 3000);
            }
        };
        
        // Start checking for session or params
        checkExistingSessionOrParams();
    }, [fetchRequestsForEmail, clearSessionData, handleTokenVerificationRef, saveSessionData]);

    // Add new useEffect to fetch access reviews when authentication is complete
    useEffect(() => {
        // Only fetch access reviews when user is authenticated
        if (isTokenVerified && email && !isInitializing) {
            console.log('Authentication complete, fetching access reviews');
            fetchAccessReviews();
        }
    }, [isTokenVerified, email, isInitializing, fetchAccessReviews]);

    // Reset authentication
    const handleSignOut = useCallback(() => {
        if (email) {
            // Use the new secure logout method
            RequestService.logout(email)
                .then(() => {
                    showSnackbar('You have been signed out successfully', 'success');
                })
                .catch(error => {
                    console.error('Error during sign out:', error);
                    showSnackbar('Error during sign out, but local session cleared', 'warning');
                });
        }
        
        // Update UI state regardless of API response
        setEmail('');
        setToken('');
        setIsEmailVerified(false);
        setIsTokenVerified(false);
        setTokenExpiry(null);
        setRequests([]);
        
        // Also call our old cleanup method as a fallback
        clearSessionData();
    }, [clearSessionData, email, showSnackbar]);

    // Handle Enter key press for email authentication
    const handleEmailKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleEmailVerification();
        }
    }, [handleEmailVerification]);

    // Handle Enter key press for token verification
    const handleTokenKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleTokenVerification();
        }
    }, [handleTokenVerification]);

    // Email authentication UI
    const renderEmailAuthentication = () => (
        <mui.Box
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                my: { xs: 3, sm: 4, md: 6 }
            }}
        >
            <mui.Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 500,
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center'
                }}
            >
                <mui.Avatar 
                    sx={{ 
                        width: 56, 
                        height: 56,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: 'primary.main'
                    }}
                >
                    <EmailIcon fontSize="large" />
                </mui.Avatar>
                
                <mui.Typography variant="h5" fontWeight={600} gutterBottom>
                    Verify Your Email
                </mui.Typography>
                
                <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Enter your email address to receive a verification code.
                    If your email is registered in our system, we'll send you a code.
                </mui.Typography>
                
                <mui.TextField
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={handleEmailKeyDown}
                    error={!!emailError}
                    helperText={emailError}
                    placeholder="your.email@company.com"
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <mui.InputAdornment position="start">
                                <EmailIcon color="action" />
                            </mui.InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />
                
                <mui.Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleEmailVerification}
                    disabled={emailLoading}
                    sx={{ 
                        py: 1.5,
                        textTransform: 'none',
                        borderRadius: 2,
                        boxShadow: 'none',
                        position: 'relative',
                        height: '48px'
                    }}
                >
                    {emailLoading ? (
                        <>
                            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                                <LogoLoader size={28} />
                            </div>
                            <span style={{ visibility: 'hidden' }}>Continue</span>
                        </>
                    ) : "Continue"}
                </mui.Button>
            </mui.Paper>
        </mui.Box>
    );

    // Token verification UI
    const renderTokenVerification = () => (
        <mui.Box
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                my: { xs: 3, sm: 4, md: 6 }
            }}
        >
            <mui.Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 500,
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center'
                }}
            >
                <mui.Avatar 
                    sx={{ 
                        width: 56, 
                        height: 56,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: 'primary.main'
                    }}
                >
                    <KeyIcon fontSize="large" />
                </mui.Avatar>
                
                <mui.Typography variant="h5" fontWeight={600} gutterBottom>
                    Enter Verification Code
                </mui.Typography>
                
                <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    We've sent a verification code to:
                </mui.Typography>
                
                <mui.Typography 
                    variant="body1" 
                    fontWeight={500} 
                    sx={{ 
                        mb: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                    }}
                >
                    <EmailIcon fontSize="small" color="primary" />
                    {email}
                </mui.Typography>
                
                {tokenExpiry && (
                    <mui.Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            mb: 2
                        }}
                    >
                        <HistoryIcon fontSize="small" />
                        Code expires in 24 hours
                    </mui.Typography>
                )}
                
                <mui.TextField
                    fullWidth
                    label="Verification Code"
                    variant="outlined"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    onKeyDown={handleTokenKeyDown}
                    error={!!tokenError}
                    helperText={tokenError}
                    placeholder="Enter the code sent to your email"
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <mui.InputAdornment position="start">
                                <KeyIcon color="action" />
                            </mui.InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />
                
                <mui.Box sx={{ display: 'flex', gap: 2 }}>
                    <mui.Button
                        variant="outlined"
                        size="large"
                        onClick={() => setIsEmailVerified(false)}
                        sx={{ 
                            py: 1.5,
                            flex: 1,
                            textTransform: 'none',
                            borderRadius: 2
                        }}
                    >
                        Back
                    </mui.Button>
                    
                    <mui.Button
                        variant="contained"
                        size="large"
                        onClick={handleTokenVerification}
                        disabled={tokenLoading}
                        sx={{ 
                            py: 1.5,
                            flex: 2,
                            textTransform: 'none',
                            borderRadius: 2,
                            boxShadow: 'none',
                            position: 'relative',
                            height: '48px'
                        }}
                    >
                        {tokenLoading ? (
                            <>
                                <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                                    <LogoLoader size={28} />
                                </div>
                                <span style={{ visibility: 'hidden' }}>Verify Code</span>
                            </>
                        ) : "Verify Code"}
                    </mui.Button>
                </mui.Box>
                
                <mui.Button
                    color="primary"
                    onClick={handleEmailVerification}
                    disabled={emailLoading}
                    sx={{ 
                        mt: 2,
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mx: 'auto'
                    }}
                >
                    Resend Code
                    {emailLoading && (
                        <LogoLoader size={20} />
                    )}
                </mui.Button>
            </mui.Paper>
        </mui.Box>
    );

    // Request dashboard UI
    const renderDashboard = () => {
        // Filter requests based on status, search term, and active view
        const filteredRequests = requests.filter(request => {
            // First check if the request matches the active view
            let matchesView;
            
            if (activeView === 'my-requests') {
                matchesView = request.REQUESTOR === email;
            } else if (activeView === 'pending-approvals') {
                // Only show pending approval requests for the current user as approver
                matchesView = request.ASSIGNED_TO === email && request.STATUS === 'Pending Approval';
            } else if (activeView === 'approval-history') {
                // Only show already processed (approved/rejected) requests for the current user as approver
                matchesView = request.ASSIGNED_TO === email && 
                             (request.STATUS === 'Approved' || request.STATUS === 'Rejected');
            } else {
                matchesView = false;
            }
            
            if (!matchesView) return false;
            
            // For 'my-requests' view, apply the status filter
            if (activeView === 'my-requests' && statusFilter !== 'all' && request.STATUS !== statusFilter) {
                return false;
            }
            
            // Then apply search term filter
            const matchesSearch = 
                !searchTerm || 
                (request.REQUEST_ID && request.REQUEST_ID.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (request.APP_NAME && request.APP_NAME.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (request.ROLES && request.ROLES.toLowerCase().includes(searchTerm.toLowerCase()));
            
            if (!matchesSearch) return false;
            
            // Finally apply date range filter
            const matchesDateRange = 
                (!dateRange[0] || !dateRange[1]) || 
                (new Date(request.DATE_REQUESTED) >= dateRange[0] && 
                 new Date(request.DATE_REQUESTED) <= dateRange[1]);
            
            return matchesDateRange;
        });
        
        // DataGrid columns definition
        const columns = [
            { 
                field: 'REQUEST_ID', 
                headerName: 'Request ID', 
                flex: 0.8,
                renderCell: (params) => (
                    <mui.Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.75rem' }}>
                        {params.value}
                    </mui.Typography>
                )
            },
            { 
                field: 'SYSTEM_NAME', 
                headerName: 'System', 
                flex: 1,
                renderCell: (params) => (
                    <mui.Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {params.value || '-'}
                    </mui.Typography>
                )
            },
            { 
                field: 'REQUESTOR', 
                headerName: 'Assignee', 
                flex: 1,
                renderCell: (params) => (
                    <mui.Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {params.row.REQUESTOR_DISPLAY || '-'}
                    </mui.Typography>
                )
            },
            { 
                field: 'ASSIGNED_TO', 
                headerName: 'Approver', 
                flex: 1,
                renderCell: (params) => (
                    <mui.Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {params.row.ASSIGNED_TO_DISPLAY || '-'}
                    </mui.Typography>
                )
            },
            { 
                field: 'ROLES', 
                headerName: 'Roles', 
                flex: 1.2,
                renderCell: (params) => {
                    if (!params.value) return '-';
                    
                    const roles = params.value.split(',');
                    if (roles.length <= 2) {
                        return (
                            <mui.Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {roles.map((role, index) => (
                                    <mui.Chip
                                        key={index}
                                        label={role.trim()}
                                        size="small"
                                        sx={{
                                            fontSize: '0.7rem',
                                            height: 20,
                                            bgcolor: alpha('#1976d2', 0.08),
                                            color: 'primary.dark',
                                        }}
                                    />
                                ))}
                            </mui.Box>
                        );
                    } else {
                        return (
                            <mui.Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <mui.Chip
                                    label={roles[0].trim()}
                                    size="small"
                                    sx={{
                                        fontSize: '0.7rem',
                                        height: 20,
                                        bgcolor: alpha('#1976d2', 0.08),
                                        color: 'primary.dark',
                                    }}
                                />
                                <mui.Chip
                                    label={`+${roles.length - 1} more`}
                                    size="small"
                                    sx={{
                                        fontSize: '0.7rem',
                                        height: 20,
                                        bgcolor: alpha('#1976d2', 0.05),
                                        color: 'primary.dark',
                                    }}
                                />
                            </mui.Box>
                        );
                    }
                }
            },
            { 
                field: 'STATUS', 
                headerName: 'Status', 
                flex: 0.8,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const status = params.value;
                    let color, bgcolor, icon;
                    
                    switch (status) {
                        case 'Approved':
                            color = 'success.dark';
                            bgcolor = alpha('#4caf50', 0.1);
                            icon = <CheckCircleOutlineIcon fontSize="small" />;
                            break;
                        case 'Rejected':
                            color = 'error.dark';
                            bgcolor = alpha('#f44336', 0.1);
                            icon = <ErrorOutlineIcon fontSize="small" />;
                            break;
                        case 'Pending Approval':
                            color = 'warning.dark';
                            bgcolor = alpha('#ff9800', 0.1);
                            icon = <HistoryIcon fontSize="small" />;
                            break;
                        default:
                            color = 'text.secondary';
                            bgcolor = alpha('#9e9e9e', 0.1);
                            icon = <HistoryIcon fontSize="small" />;
                    }
                    
                    return (
                        <mui.Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <mui.Chip
                                icon={icon}
                                label={status}
                                size="small"
                                sx={{
                                    bgcolor,
                                    color,
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    height: 20,
                                    '& .MuiChip-icon': {
                                        color: 'inherit',
                                        fontSize: '0.9rem'
                                    }
                                }}
                            />
                        </mui.Box>
                    );
                }
            },
            { 
                field: 'DATE_REQUESTED', 
                headerName: 'Date Requested', 
                flex: 0.8,
                renderCell: (params) => {
                    if (!params.value) return '-';
                    try {
                        const date = new Date(params.value);
                        if (isNaN(date.getTime())) return '-';
                        return (
                            <mui.Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                })}
                            </mui.Typography>
                        );
                    } catch (error) {
                        console.error('Error formatting date:', error);
                        return '-';
                    }
                }
            },
            {
                field: 'actions',
                headerName: 'Actions',
                flex: 0.5,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <mui.Box sx={{ 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center',
                        alignItems: 'center', 
                        height: '100%'
                    }}>
                        <mui.Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                handleRequestSelect(params.row);
                            }}
                            sx={{
                                minWidth: 'auto',
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                px: 1
                            }}
                        >
                            View
                        </mui.Button>
                    </mui.Box>
                )
            }
        ];

        return (
            <mui.Box sx={{ 
                width: '100%', 
                py: 3, 
                display: 'flex', 
                gap: isSidebarCollapsed ? 2 : 3 
            }}>
                {/* Premium Sidebar */}
                <mui.Paper
                    elevation={0}
                    sx={{
                        width: isSidebarCollapsed ? '80px' : '260px',
                        minWidth: isSidebarCollapsed ? '80px' : '260px',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        height: 'fit-content',
                        flexShrink: 0
                    }}
                >
                    <mui.Box 
                        sx={{ 
                            p: 2, 
                            borderBottom: '1px solid', 
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isSidebarCollapsed ? 'center' : 'space-between',
                            minHeight: '64px'
                        }}
                    >
                        {!isSidebarCollapsed && (
                            <mui.Typography 
                                variant="h6" 
                                fontWeight={600}
                                sx={{ 
                                    opacity: isSidebarCollapsed ? 0 : 1,
                                    transition: 'opacity 0.2s ease',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Dashboard Views
                            </mui.Typography>
                        )}
                        <mui.IconButton 
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            size="small"
                            sx={{
                                bgcolor: alpha('#1976d2', 0.04),
                                '&:hover': {
                                    bgcolor: alpha('#1976d2', 0.08)
                                }
                            }}
                        >
                            {isSidebarCollapsed ? 
                                <mui.Icon>chevron_right</mui.Icon> : 
                                <mui.Icon>chevron_left</mui.Icon>
                            }
                        </mui.IconButton>
                    </mui.Box>
                    
                    <mui.List sx={{ width: '100%', p: 1 }}>
                        <mui.ListItemButton
                            selected={activeView === 'my-requests'}
                            onClick={() => setActiveView('my-requests')}
                            sx={{
                                borderRadius: 2,
                                mb: 1,
                                py: 1.5,
                                px: isSidebarCollapsed ? 1 : 2,
                                '&.Mui-selected': {
                                    bgcolor: alpha('#1976d2', 0.08),
                                    '&:hover': {
                                        bgcolor: alpha('#1976d2', 0.12)
                                    }
                                },
                                position: 'relative',
                                minHeight: '48px',
                                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start'
                            }}
                        >
                            <mui.ListItemIcon 
                                sx={{ 
                                    minWidth: isSidebarCollapsed ? 'auto' : 40,
                                    mr: isSidebarCollapsed ? 0 : 2
                                }}
                            >
                                <AssignmentIcon color={activeView === 'my-requests' ? 'primary' : 'action'} />
                            </mui.ListItemIcon>
                            {!isSidebarCollapsed && (
                                <mui.ListItemText
                                    primary="My Requests"
                                    secondary={`${requests.filter(r => r.REQUESTOR === email).length} requests`}
                                    primaryTypographyProps={{ 
                                        fontWeight: 500,
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                    secondaryTypographyProps={{
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                />
                            )}
                            {isSidebarCollapsed && (
                                <mui.Badge
                                    badgeContent={requests.filter(r => r.REQUESTOR === email).length}
                                    color="primary"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.6rem',
                                            height: 16,
                                            minWidth: 16
                                        }
                                    }}
                                />
                            )}
                        </mui.ListItemButton>
                        
                        <mui.ListItemButton
                            selected={activeView === 'pending-approvals'}
                            onClick={() => setActiveView('pending-approvals')}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                mb: 1,
                                px: isSidebarCollapsed ? 1 : 2,
                                '&.Mui-selected': {
                                    bgcolor: alpha('#1976d2', 0.08),
                                    '&:hover': {
                                        bgcolor: alpha('#1976d2', 0.12)
                                    }
                                },
                                position: 'relative',
                                minHeight: '48px',
                                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start'
                            }}
                        >
                            <mui.ListItemIcon 
                                sx={{ 
                                    minWidth: isSidebarCollapsed ? 'auto' : 40,
                                    mr: isSidebarCollapsed ? 0 : 2
                                }}
                            >
                                <LockPersonRoundedIcon color={activeView === 'pending-approvals' ? 'primary' : 'action'} />
                            </mui.ListItemIcon>
                            {!isSidebarCollapsed && (
                                <mui.ListItemText
                                    primary="Pending Approvals"
                                    secondary={`${requests.filter(r => r.ASSIGNED_TO === email && r.STATUS === 'Pending Approval').length} pending`}
                                    primaryTypographyProps={{ 
                                        fontWeight: 500,
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                    secondaryTypographyProps={{
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                />
                            )}
                            {isSidebarCollapsed && (
                                <mui.Badge
                                    badgeContent={requests.filter(r => r.ASSIGNED_TO === email && r.STATUS === 'Pending Approval').length}
                                    color="warning"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.6rem',
                                            height: 16,
                                            minWidth: 16
                                        }
                                    }}
                                />
                            )}
                        </mui.ListItemButton>
                        
                        <mui.ListItemButton
                            selected={activeView === 'approval-history'}
                            onClick={() => setActiveView('approval-history')}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                px: isSidebarCollapsed ? 1 : 2,
                                '&.Mui-selected': {
                                    bgcolor: alpha('#1976d2', 0.08),
                                    '&:hover': {
                                        bgcolor: alpha('#1976d2', 0.12)
                                    }
                                },
                                position: 'relative',
                                minHeight: '48px',
                                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start'
                            }}
                        >
                            <mui.ListItemIcon 
                                sx={{ 
                                    minWidth: isSidebarCollapsed ? 'auto' : 40,
                                    mr: isSidebarCollapsed ? 0 : 2
                                }}
                            >
                                <HistoryIcon color={activeView === 'approval-history' ? 'primary' : 'action'} />
                            </mui.ListItemIcon>
                            {!isSidebarCollapsed && (
                                <mui.ListItemText
                                    primary="Approval History"
                                    secondary={`${requests.filter(r => r.ASSIGNED_TO === email && (r.STATUS === 'Approved' || r.STATUS === 'Rejected')).length} processed`}
                                    primaryTypographyProps={{ 
                                        fontWeight: 500,
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                    secondaryTypographyProps={{
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                />
                            )}
                            {isSidebarCollapsed && (
                                <mui.Badge
                                    badgeContent={requests.filter(r => r.ASSIGNED_TO === email && (r.STATUS === 'Approved' || r.STATUS === 'Rejected')).length}
                                    color="default"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.6rem',
                                            height: 16,
                                            minWidth: 16
                                        }
                                    }}
                                />
                            )}
                        </mui.ListItemButton>
                        
                        {/* Add new Access Reviews list item */}
                        <mui.ListItemButton
                            selected={activeView === 'access-reviews'}
                            onClick={() => {
                                setActiveView('access-reviews');
                                fetchAccessReviews();
                            }}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                px: isSidebarCollapsed ? 1 : 2,
                                '&.Mui-selected': {
                                    bgcolor: alpha('#1976d2', 0.08),
                                    '&:hover': {
                                        bgcolor: alpha('#1976d2', 0.12)
                                    }
                                },
                                position: 'relative',
                                minHeight: '48px',
                                justifyContent: isSidebarCollapsed ? 'center' : 'flex-start'
                            }}
                        >
                            <mui.ListItemIcon 
                                sx={{ 
                                    minWidth: isSidebarCollapsed ? 'auto' : 40,
                                    mr: isSidebarCollapsed ? 0 : 2
                                }}
                            >
                                <SecurityIcon color={activeView === 'access-reviews' ? 'primary' : 'action'} />
                            </mui.ListItemIcon>
                            {!isSidebarCollapsed && (
                                <mui.ListItemText
                                    primary="Access Reviews"
                                    secondary={`${accessReviews.filter(r => !r.STATUS).length} pending`}
                                    primaryTypographyProps={{ 
                                        fontWeight: 500,
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                    secondaryTypographyProps={{
                                        sx: { whiteSpace: 'nowrap' }
                                    }}
                                />
                            )}
                            {isSidebarCollapsed && (
                                <mui.Badge
                                    badgeContent={accessReviews.filter(r => !r.STATUS).length}
                                    color="warning"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.6rem',
                                            height: 16,
                                            minWidth: 16
                                        }
                                    }}
                                />
                            )}
                        </mui.ListItemButton>
                    </mui.List>
                </mui.Paper>

                {/* Main Content */}
                <mui.Box sx={{ flex: 1, maxWidth: isSidebarCollapsed ? 'calc(100% - 100px)' : 'calc(100% - 280px)' }}>
                    <mui.Paper 
                        elevation={0}
                        sx={{ 
                            p: { xs: 2, sm: 3 }, 
                            borderRadius: 3, 
                            mb: 3,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <mui.Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 3,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <mui.Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                                <mui.Typography variant="h6" fontWeight={600}>
                                    {activeView === 'my-requests' 
                                        ? 'My Access Requests' 
                                        : activeView === 'pending-approvals'
                                            ? 'Pending Approvals'
                                            : activeView === 'approval-history'
                                                ? 'Approval History'
                                                : 'Access Reviews'}
                                </mui.Typography>
                                <mui.Typography variant="body2" color="text.secondary">
                                    {email}
                                </mui.Typography>
                            </mui.Box>
                            
                            <mui.Box sx={{ 
                                display: 'flex', 
                                gap: 2,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'space-between', sm: 'flex-end' }
                            }}>
                                {activeView === 'my-requests' && (
                                    <mui.Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<KeyIcon />}
                                        onClick={() => navigate('/request/form')}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 500,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        Create Request
                                    </mui.Button>
                                )}
                                
                                <mui.Button
                                    variant="outlined"
                                    onClick={handleSignOut}
                                    size="small"
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: 2,
                                        px: 2
                                    }}
                                >
                                    Sign Out
                                </mui.Button>
                            </mui.Box>
                        </mui.Box>
                        
                        {/* Mobile View Selector */}
                        <mui.Box sx={{ 
                            display: { xs: 'flex', md: 'none' },
                            gap: 1,
                            mb: 3,
                            flexWrap: 'wrap'
                        }}>
                            <mui.Button
                                variant={activeView === 'my-requests' ? 'contained' : 'outlined'}
                                onClick={() => setActiveView('my-requests')}
                                startIcon={<AssignmentIcon />}
                                sx={{ flex: 1, textTransform: 'none' }}
                                size="small"
                            >
                                My Requests
                            </mui.Button>
                            <mui.Button
                                variant={activeView === 'pending-approvals' ? 'contained' : 'outlined'}
                                onClick={() => setActiveView('pending-approvals')}
                                startIcon={<LockPersonRoundedIcon />}
                                sx={{ flex: 1, textTransform: 'none' }}
                                size="small"
                            >
                                Pending
                            </mui.Button>
                            <mui.Button
                                variant={activeView === 'approval-history' ? 'contained' : 'outlined'}
                                onClick={() => setActiveView('approval-history')}
                                startIcon={<HistoryIcon />}
                                sx={{ flex: 1, textTransform: 'none' }}
                                size="small"
                            >
                                History
                            </mui.Button>
                            <mui.Button
                                variant={activeView === 'access-reviews' ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setActiveView('access-reviews');
                                    fetchAccessReviews();
                                }}
                                startIcon={<SecurityIcon />}
                                sx={{ flex: 1, textTransform: 'none' }}
                                size="small"
                            >
                                Reviews
                            </mui.Button>
                        </mui.Box>
                        
                        {/* Show search/filter UI only for request views */}
                        {activeView !== 'access-reviews' && (
                        <mui.Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 2, 
                            mb: 3,
                            justifyContent: { xs: 'flex-start', sm: 'space-between' }
                        }}>
                            {/* Search input */}
                            <mui.TextField
                                placeholder="Search requests..."
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ 
                                    flex: { xs: '1 1 100%', sm: '1 1 auto' },
                                    maxWidth: { sm: 300 }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <mui.InputAdornment position="start">
                                            <mui.Icon>search</mui.Icon>
                                        </mui.InputAdornment>
                                    ),
                                }}
                            />
                            
                            {/* Status filter - only show for My Requests view */}
                            {activeView === 'my-requests' && (
                                <mui.FormControl 
                                    variant="outlined" 
                                    size="small" 
                                    sx={{ 
                                        minWidth: 150,
                                        flex: { xs: '1 1 auto', sm: 'auto' }
                                    }}
                                >
                                    <mui.InputLabel id="status-filter-label">Status</mui.InputLabel>
                                    <mui.Select
                                        labelId="status-filter-label"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        label="Status"
                                    >
                                        <mui.MenuItem value="all">All Statuses</mui.MenuItem>
                                        <mui.MenuItem value="Pending Approval">Pending Approval</mui.MenuItem>
                                        <mui.MenuItem value="Approved">Approved</mui.MenuItem>
                                        <mui.MenuItem value="Rejected">Rejected</mui.MenuItem>
                                    </mui.Select>
                                </mui.FormControl>
                            )}

                            {/* Status filter specifically for Approval History view */}
                            {activeView === 'approval-history' && (
                                <mui.FormControl 
                                    variant="outlined" 
                                    size="small" 
                                    sx={{ 
                                        minWidth: 150,
                                        flex: { xs: '1 1 auto', sm: 'auto' }
                                    }}
                                >
                                    <mui.InputLabel id="approval-status-filter-label">Status</mui.InputLabel>
                                    <mui.Select
                                        labelId="approval-status-filter-label"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        label="Status"
                                    >
                                        <mui.MenuItem value="all">All Responses</mui.MenuItem>
                                        <mui.MenuItem value="Approved">Approved</mui.MenuItem>
                                        <mui.MenuItem value="Rejected">Rejected</mui.MenuItem>
                                    </mui.Select>
                                </mui.FormControl>
                            )}
                        </mui.Box>
                        )}
                        
                        {/* DataGrid for requests - only show for request views */}
                        {activeView !== 'access-reviews' && (
                        <mui.Box sx={{ height: 400, width: '100%' }}>
                            {loading ? (
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    height: '100%',
                                    flexDirection: 'column',
                                    gap: 2
                                }}>
                                    <LogoLoader size={50} message="Loading your requests..." />
                                </mui.Box>
                            ) : filteredRequests.length === 0 ? (
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    height: '100%',
                                    flexDirection: 'column',
                                    gap: 2,
                                    p: 3,
                                    textAlign: 'center',
                                    bgcolor: alpha('#f5f5f5', 0.5),
                                    borderRadius: 2,
                                    border: '1px dashed',
                                    borderColor: 'divider'
                                }}>
                                    <AssignmentIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.6 }} />
                                    
                                    {requests.length === 0 ? (
                                        <>
                                            <mui.Typography variant="h6" color="text.secondary">
                                                No Requests Found
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                                                You haven't submitted any access requests yet. When you do, they will appear here.
                                            </mui.Typography>
                                        </>
                                    ) : (
                                        <>
                                            <mui.Typography variant="h6" color="text.secondary">
                                                No Matching Requests
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                                                No requests match your current filters. Try adjusting your search criteria or view all requests.
                                            </mui.Typography>
                                            {statusFilter !== 'all' && (
                                                <mui.Button 
                                                    variant="outlined" 
                                                    size="small"
                                                    onClick={() => setStatusFilter('all')}
                                                    sx={{ mt: 1, textTransform: 'none' }}
                                                >
                                                    Show All Requests
                                                </mui.Button>
                                            )}
                                        </>
                                    )}
                                </mui.Box>
                            ) : (
                                <DataGrid
                                    rows={filteredRequests}
                                    columns={columns}
                                    pageSize={5}
                                    rowsPerPageOptions={[5, 10, 20]}
                                    getRowId={(row) => row.id || Math.random().toString()}
                                    disableSelectionOnClick
                                    disableColumnMenu
                                    sx={{
                                        border: 'none',
                                        '& .MuiDataGrid-columnHeaders': {
                                            bgcolor: alpha('#f5f5f5', 0.5)
                                        },
                                        '& .MuiDataGrid-cell': {
                                            py: 1.5,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }
                                    }}
                                />
                            )}
                        </mui.Box>
                        )}
                        
                        {/* Add Access Reviews content */}
                        {activeView === 'access-reviews' && (
                            <UARReviewDashboard email={email} showSnackbar={showSnackbar} />
                        )}
                    
                    {/* Status summary cards - only show relevant statuses based on view */}
                        {activeView !== 'access-reviews' && (
                    <mui.Box sx={{ px: 0 }}>
                        <mui.Grid container spacing={3}>
                            {[
                                ...(activeView === 'my-requests' ? [
                                    { 
                                        status: 'Pending Approval', 
                                        color: '#ff9800', 
                                        icon: <HistoryIcon fontSize="large" />,
                                        count: requests.filter(r => r.REQUESTOR === email && r.STATUS === 'Pending Approval').length
                                    },
                                    { 
                                        status: 'Approved', 
                                        color: '#4caf50', 
                                        icon: <CheckCircleOutlineIcon fontSize="large" />,
                                        count: requests.filter(r => r.REQUESTOR === email && r.STATUS === 'Approved').length
                                    },
                                    { 
                                        status: 'Rejected', 
                                        color: '#f44336', 
                                        icon: <ErrorOutlineIcon fontSize="large" />,
                                        count: requests.filter(r => r.REQUESTOR === email && r.STATUS === 'Rejected').length
                                    }
                                ] : [
                                    { 
                                        status: 'Pending Approval', 
                                        color: '#ff9800', 
                                        icon: <HistoryIcon fontSize="large" />,
                                        count: requests.filter(r => r.ASSIGNED_TO === email && r.STATUS === 'Pending Approval').length
                                    },
                                    { 
                                        status: 'Approved', 
                                        color: '#4caf50', 
                                        icon: <CheckCircleOutlineIcon fontSize="large" />,
                                        count: requests.filter(r => r.ASSIGNED_TO === email && r.STATUS === 'Approved').length
                                    },
                                    { 
                                        status: 'Rejected', 
                                        color: '#f44336', 
                                        icon: <ErrorOutlineIcon fontSize="large" />,
                                        count: requests.filter(r => r.ASSIGNED_TO === email && r.STATUS === 'Rejected').length
                                    }
                                ])
                            ].map((item) => (
                                <mui.Grid item xs={12} sm={4} key={item.status}>
                                    <mui.Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            height: '100%',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                            }
                                        }}
                                        onClick={() => setStatusFilter(item.status)}
                                    >
                                        <mui.Avatar
                                            sx={{
                                                bgcolor: alpha(item.color, 0.1),
                                                color: item.color,
                                                width: 52,
                                                height: 52
                                            }}
                                        >
                                            {item.icon}
                                        </mui.Avatar>
                                        
                                        <mui.Box>
                                            <mui.Typography variant="h4" fontWeight={600}>
                                                {item.count}
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                {item.status}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Paper>
                                </mui.Grid>
                            ))}
                        </mui.Grid>
                    </mui.Box>
                        )}
                    
                    {/* Request details dialog */}
                    <mui.Dialog
                        open={detailsOpen}
                        onClose={() => setDetailsOpen(false)}
                        maxWidth="md"
                        PaperProps={{
                            sx: {
                                borderRadius: 3,
                                overflow: 'hidden',
                                minWidth: { xs: '100%', sm: 500 }
                            }
                        }}
                    >
                        {selectedRequest && (
                            <>
                                <mui.DialogTitle
                                    sx={{
                                        p: 3,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                    }}
                                >
                                    <AssignmentIcon sx={{ color: 'inherit' }} />
                                    <mui.Box>
                                        <mui.Typography variant="subtitle1" fontWeight={600}>
                                            Request Details
                                        </mui.Typography>
                                        <mui.Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {selectedRequest.REQUEST_ID}
                                        </mui.Typography>
                                    </mui.Box>
                                </mui.DialogTitle>
                                
                                <mui.DialogContent sx={{ p: 3 }}>
                                    <mui.Grid container spacing={3}>
                                        <mui.Grid item xs={12}>
                                            <mui.Box 
                                                sx={{ 
                                                    p: 2, 
                                                    bgcolor: alpha('#f8f9fa', 0.5),
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    mb: 2
                                                }}
                                            >
                                                <mui.Typography variant="subtitle1" fontWeight={600}>
                                                    Status
                                                </mui.Typography>
                                                {(() => {
                                                    const status = selectedRequest.STATUS;
                                                    let color, bgcolor, icon;
                                                    
                                                    switch (status) {
                                                        case 'Approved':
                                                            color = 'success.dark';
                                                            bgcolor = alpha('#4caf50', 0.1);
                                                            icon = <CheckCircleOutlineIcon />;
                                                            break;
                                                        case 'Rejected':
                                                            color = 'error.dark';
                                                            bgcolor = alpha('#f44336', 0.1);
                                                            icon = <ErrorOutlineIcon />;
                                                            break;
                                                        case 'Pending Approval':
                                                            color = 'warning.dark';
                                                            bgcolor = alpha('#ff9800', 0.1);
                                                            icon = <HistoryIcon />;
                                                            break;
                                                        default:
                                                            color = 'text.secondary';
                                                            bgcolor = alpha('#9e9e9e', 0.1);
                                                            icon = <HistoryIcon />;
                                                    }
                                                    
                                                    return (
                                                        <mui.Chip
                                                            icon={icon}
                                                            label={status}
                                                            sx={{
                                                                bgcolor,
                                                                color,
                                                                fontSize: '0.875rem',
                                                                fontWeight: 600,
                                                                height: 36,
                                                                px: 1,
                                                                '& .MuiChip-icon': {
                                                                    color: 'inherit'
                                                                }
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </mui.Box>
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12} sm={6}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                System
                                            </mui.Typography>
                                            <mui.Typography variant="body1" fontWeight={500}>
                                                {selectedRequest.SYSTEM_NAME || '-'}
                                            </mui.Typography>
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12} sm={6}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                Request Type
                                            </mui.Typography>
                                            <mui.Typography variant="body1" fontWeight={500}>
                                                {selectedRequest.REQUEST_TYPE || '-'}
                                            </mui.Typography>
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12} sm={6}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                Date Requested
                                            </mui.Typography>
                                            <mui.Typography variant="body1" fontWeight={500}>
                                                {selectedRequest.DATE_REQUESTED
                                                    ? new Date(selectedRequest.DATE_REQUESTED).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                      })
                                                    : '-'
                                                }
                                            </mui.Typography>
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12} sm={6}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                Last Modified
                                            </mui.Typography>
                                            <mui.Typography variant="body1" fontWeight={500}>
                                                {selectedRequest.LAST_MODIFIED
                                                    ? new Date(selectedRequest.LAST_MODIFIED).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                      })
                                                    : '-'
                                                }
                                            </mui.Typography>
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                Requested Roles
                                            </mui.Typography>
                                            <mui.Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {selectedRequest.ROLES
                                                    ? selectedRequest.ROLES.split(',').map((role, index) => (
                                                        <mui.Chip
                                                            key={index}
                                                            label={role.trim()}
                                                            sx={{
                                                                bgcolor: alpha('#1976d2', 0.08),
                                                                color: 'primary.dark',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                      ))
                                                    : <mui.Typography variant="body2" fontStyle="italic">No roles specified</mui.Typography>
                                                }
                                            </mui.Box>
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12} sm={6}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                Assignee
                                            </mui.Typography>
                                            <mui.Typography variant="body1" fontWeight={500}>
                                                {selectedRequest.REQUESTOR_DISPLAY || '-'}
                                                {selectedRequest.REQUESTOR_TITLE && (
                                                    <mui.Typography 
                                                        component="span" 
                                                        variant="body2" 
                                                        color="text.secondary" 
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        ({selectedRequest.REQUESTOR_TITLE})
                                                    </mui.Typography>
                                                )}
                                            </mui.Typography>
                                            {selectedRequest.REQUESTOR_EMAIL && selectedRequest.REQUESTOR_EMAIL !== selectedRequest.REQUESTOR_DISPLAY && (
                                                <mui.Typography variant="caption" color="text.secondary">
                                                    {selectedRequest.REQUESTOR_EMAIL}
                                                </mui.Typography>
                                            )}
                                        </mui.Grid>
                                        
                                        <mui.Grid item xs={12} sm={6}>
                                            <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                Approver
                                            </mui.Typography>
                                            <mui.Typography variant="body1" fontWeight={500}>
                                                {selectedRequest.ASSIGNED_TO_DISPLAY || '-'}
                                                {selectedRequest.ASSIGNED_TO_TITLE && (
                                                    <mui.Typography 
                                                        component="span" 
                                                        variant="body2" 
                                                        color="text.secondary" 
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        ({selectedRequest.ASSIGNED_TO_TITLE})
                                                    </mui.Typography>
                                                )}
                                            </mui.Typography>
                                            {selectedRequest.ASSIGNED_TO_EMAIL && selectedRequest.ASSIGNED_TO_EMAIL !== selectedRequest.ASSIGNED_TO_DISPLAY && (
                                                <mui.Typography variant="caption" color="text.secondary">
                                                    {selectedRequest.ASSIGNED_TO_EMAIL}
                                                </mui.Typography>
                                            )}
                                        </mui.Grid>
                                        
                                        {selectedRequest.COMMENTS && (
                                            <mui.Grid item xs={12}>
                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                    Comments
                                                </mui.Typography>
                                                <mui.Box 
                                                    sx={{ 
                                                        mt: 1,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        bgcolor: alpha('#f8f9fa', 0.5),
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <mui.Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                        "{selectedRequest.COMMENTS}"
                                                    </mui.Typography>
                                                </mui.Box>
                                            </mui.Grid>
                                        )}
                                        
                                        {/* Add approval comments field when in pending-approvals view and status is Pending Approval */}
                                        {activeView === 'pending-approvals' && selectedRequest.STATUS === 'Pending Approval' && (
                                            <mui.Grid item xs={12}>
                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                    Approval Comments (Optional)
                                                </mui.Typography>
                                                <mui.TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    placeholder="Add comments about this approval/rejection"
                                                    value={approvalComments}
                                                    onChange={(e) => setApprovalComments(e.target.value)}
                                                    variant="outlined"
                                                    sx={{ mt: 1 }}
                                                />
                                            </mui.Grid>
                                        )}
                                        
                                        {/* Show approval details for approved/rejected requests */}
                                        {(selectedRequest?.STATUS === 'Approved' || selectedRequest?.STATUS === 'Rejected') && (
                                            <mui.Grid item xs={12}>
                                                <mui.Divider sx={{ my: 2 }} />
                                                <mui.Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                                    {selectedRequest.STATUS === 'Approved' ? 'Approval Details' : 'Rejection Details'}
                                                </mui.Typography>
                                                
                                                {approvalDetailsLoading ? (
                                                    <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                                        <mui.CircularProgress size={24} />
                                                    </mui.Box>
                                                ) : approvalDetails ? (
                                                    <mui.Grid container spacing={2}>
                                                        {/* Approval date */}
                                                        {selectedRequest.STATUS === 'Approved' && approvalDetails.DATE_APPROVED && (
                                                            <mui.Grid item xs={12} sm={6}>
                                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                    Date Approved
                                                                </mui.Typography>
                                                                <mui.Typography variant="body1">
                                                                    {new Date(approvalDetails.DATE_APPROVED).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </mui.Typography>
                                                            </mui.Grid>
                                                        )}
                                                        
                                                        {/* Rejection date */}
                                                        {selectedRequest.STATUS === 'Rejected' && approvalDetails.DATE_REJECTED && (
                                                            <mui.Grid item xs={12} sm={6}>
                                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                    Date Rejected
                                                                </mui.Typography>
                                                                <mui.Typography variant="body1">
                                                                    {new Date(approvalDetails.DATE_REJECTED).toLocaleDateString('en-US', {
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric',
                                                                        hour: '2-digit',
                                                                        minute: '2-digit'
                                                                    })}
                                                                </mui.Typography>
                                                            </mui.Grid>
                                                        )}
                                                        
                                                        {/* Approved/Rejected by */}
                                                        {approvalDetails.APPROVED_BY && (
                                                            <mui.Grid item xs={12} sm={6}>
                                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                    {selectedRequest.STATUS === 'Approved' ? 'Approved By' : 'Rejected By'}
                                                                </mui.Typography>
                                                                <mui.Typography variant="body1">
                                                                    {approvalDetails.APPROVED_BY_NAME || selectedRequest.ASSIGNED_TO_DISPLAY || selectedRequest.ASSIGNED_TO || 'Unknown'}
                                                                </mui.Typography>
                                                            </mui.Grid>
                                                        )}
                                                        
                                                        {/* Rejection reason */}
                                                        {selectedRequest.STATUS === 'Rejected' && approvalDetails.REJECTION_REASON && (
                                                            <mui.Grid item xs={12}>
                                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                    Rejection Reason
                                                                </mui.Typography>
                                                                <mui.Box 
                                                                    sx={{ 
                                                                        mt: 1,
                                                                        p: 2,
                                                                        borderRadius: 2,
                                                                        bgcolor: alpha('#f8f9fa', 0.5),
                                                                        border: '1px solid',
                                                                        borderColor: 'divider'
                                                                    }}
                                                                >
                                                                    <mui.Typography variant="body2">
                                                                        {approvalDetails.REJECTION_REASON}
                                                                    </mui.Typography>
                                                                </mui.Box>
                                                            </mui.Grid>
                                                        )}
                                                        
                                                        {/* Approver comments */}
                                                        {approvalDetails.COMMENTS && (
                                                            <mui.Grid item xs={12}>
                                                                <mui.Typography variant="caption" color="text.secondary" fontWeight={500}>
                                                                    Approver Comments
                                                                </mui.Typography>
                                                                <mui.Box 
                                                                    sx={{ 
                                                                        mt: 1,
                                                                        p: 2,
                                                                        borderRadius: 2,
                                                                        bgcolor: alpha('#f8f9fa', 0.5),
                                                                        border: '1px solid',
                                                                        borderColor: 'divider'
                                                                    }}
                                                                >
                                                                    <mui.Typography variant="body2">
                                                                        {approvalDetails.COMMENTS}
                                                                    </mui.Typography>
                                                                </mui.Box>
                                                            </mui.Grid>
                                                        )}
                                                    </mui.Grid>
                                                ) : (
                                                    <mui.Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        No detailed approval information available
                                                    </mui.Typography>
                                                )}
                                            </mui.Grid>
                                        )}
                                    </mui.Grid>
                                </mui.DialogContent>
                                
                                <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <mui.Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                                        {/* Delete button (only show for My Requests view and for pending requests) */}
                                        {activeView === 'my-requests' && 
                                         selectedRequest && 
                                         selectedRequest.STATUS === 'Pending Approval' && 
                                         selectedRequest.REQUESTOR === email && (
                                            <mui.Button 
                                                variant="outlined" 
                                                color="error"
                                                onClick={openDeleteDialog}
                                                startIcon={<DeleteIcon />}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Delete Request
                                            </mui.Button>
                                        )}
                                        
                                        <mui.Box>
                                            <mui.Button onClick={() => setDetailsOpen(false)} sx={{ textTransform: 'none' }}>
                                                Close
                                            </mui.Button>
                                            
                                            {/* Show approval/rejection buttons only for pending approvals that the current user is assigned to approve */}
                                            {activeView === 'pending-approvals' && 
                                            selectedRequest.STATUS === 'Pending Approval' && 
                                            selectedRequest.ASSIGNED_TO === email && (
                                                <>
                                                    <mui.Button 
                                                        variant="outlined" 
                                                        color="error"
                                                        onClick={openRejectionDialog}
                                                        disabled={approvalLoading}
                                                        sx={{ textTransform: 'none', ml: 1 }}
                                                    >
                                                        Reject
                                                    </mui.Button>
                                                    
                                                    <mui.Button 
                                                        variant="contained" 
                                                        color="success"
                                                        onClick={handleApproveRequest}
                                                        disabled={approvalLoading}
                                                        sx={{ textTransform: 'none', ml: 1 }}
                                                        startIcon={approvalLoading ? <mui.CircularProgress size={20} /> : null}
                                                    >
                                                        Approve
                                                    </mui.Button>
                                                </>
                                            )}
                                        </mui.Box>
                                    </mui.Box>
                                </mui.DialogActions>
                            </>
                        )}
                    </mui.Dialog>
                    
                    {/* Rejection reason dialog */}
                    {renderRejectionDialog()}
                    
                    {/* Delete confirmation dialog */}
                    {renderDeleteConfirmationDialog()}
                    </mui.Paper>
                </mui.Box>
            </mui.Box>
        );
    };

    // Modify the renderContent function to include the dashboard
    const renderContent = () => {
        if (isInitializing) {
            return (
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: '70vh',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <LogoLoader size={70} message="Preparing your dashboard..." />
                </mui.Box>
            );
        } else if (!isEmailVerified) {
            return renderEmailAuthentication();
        } else if (!isTokenVerified) {
            return renderTokenVerification();
        } else {
            return renderDashboard();
        }
    };

    // Handle request approval
    const handleApproveRequest = useCallback(async () => {
        if (!selectedRequest || !email) return;
        
        setApprovalLoading(true);
        
        try {
            const response = await RequestService.approveRequest({
                id: selectedRequest.id,
                approverEmail: email,
                comments: approvalComments
            });
            
            // Close the dialog
            setDetailsOpen(false);
            
            // Show success message
            showSnackbar(`Request ${selectedRequest.REQUEST_ID} has been approved successfully`, 'success');
            
            // Refresh the requests list
            fetchRequests();
            
            // Clear approval comments
            setApprovalComments('');
        } catch (error) {
            console.error('Error approving request:', error);
            showSnackbar('Failed to approve request. Please try again.', 'error');
        } finally {
            setApprovalLoading(false);
        }
    }, [selectedRequest, email, approvalComments, showSnackbar, fetchRequests]);
    
    // Handle request rejection
    const handleRejectRequest = useCallback(async () => {
        if (!selectedRequest || !email || !rejectionReason.trim()) return;
        
        setApprovalLoading(true);
        
        try {
            const response = await RequestService.rejectRequest({
                id: selectedRequest.id,
                approverEmail: email,
                comments: approvalComments,
                rejectionReason: rejectionReason
            });
            
            // Close dialogs
            setRejectionDialogOpen(false);
            setDetailsOpen(false);
            
            // Show success message
            showSnackbar(`Request ${selectedRequest.REQUEST_ID} has been rejected`, 'success');
            
            // Refresh the requests list
            fetchRequests();
            
            // Clear rejection reason and comments
            setRejectionReason('');
            setApprovalComments('');
        } catch (error) {
            console.error('Error rejecting request:', error);
            showSnackbar('Failed to reject request. Please try again.', 'error');
        } finally {
            setApprovalLoading(false);
        }
    }, [selectedRequest, email, rejectionReason, approvalComments, showSnackbar, fetchRequests]);
    
    // Open rejection dialog
    const openRejectionDialog = useCallback(() => {
        setRejectionDialogOpen(true);
    }, []);

    // Add a dialog for rejection reason
    const renderRejectionDialog = () => (
        <mui.Dialog
            open={rejectionDialogOpen}
            onClose={() => setRejectionDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    p: 2
                }
            }}
        >
            <mui.DialogTitle sx={{ pb: 1 }}>
                Reject Request
            </mui.DialogTitle>
            
            <mui.DialogContent>
                <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Please provide a reason for rejecting this request. This information will be shared with the requester.
                </mui.Typography>
                
                <mui.TextField
                    label="Rejection Reason"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    error={approvalLoading && !rejectionReason.trim()}
                    helperText={approvalLoading && !rejectionReason.trim() ? "Rejection reason is required" : ""}
                    sx={{ mb: 2 }}
                />
            </mui.DialogContent>
            
            <mui.DialogActions>
                <mui.Button 
                    onClick={() => setRejectionDialogOpen(false)}
                    disabled={approvalLoading}
                >
                    Cancel
                </mui.Button>
                
                <mui.Button 
                    variant="contained" 
                    color="error"
                    disabled={approvalLoading || !rejectionReason.trim()}
                    onClick={handleRejectRequest}
                    startIcon={approvalLoading ? <mui.CircularProgress size={20} /> : null}
                >
                    Reject Request
                </mui.Button>
            </mui.DialogActions>
        </mui.Dialog>
    );

    // Fetch approval details for a request
    const fetchApprovalDetails = useCallback(async (requestId) => {
        if (!requestId) return null;
        
        setApprovalDetailsLoading(true);
        
        try {
            // First try to fetch approval record by request ID
            const response = await RequestService.fetchApprovalByRequestId(requestId);
            
            if (response) {
                console.log('Fetched approval details:', response);
                setApprovalDetails(response);
            } else {
                console.log('No approval details found for request:', requestId);
                setApprovalDetails(null);
            }
        } catch (error) {
            console.error('Error fetching approval details:', error);
            setApprovalDetails(null);
        } finally {
            setApprovalDetailsLoading(false);
        }
    }, []);

    // Enhanced function to handle request selection
    const handleRequestSelect = useCallback((request) => {
        setSelectedRequest(request);
        
        // If the request is approved or rejected, fetch approval details
        if (request && (request.STATUS === 'Approved' || request.STATUS === 'Rejected')) {
            fetchApprovalDetails(request.id);
        } else {
            setApprovalDetails(null);
        }
        
        setDetailsOpen(true);
    }, [fetchApprovalDetails]);

    // Handle request deletion
    const handleDeleteRequest = useCallback(async () => {
        if (!selectedRequest || !email) return;
        
        setDeleteLoading(true);
        
        try {
            await RequestService.deleteUserRequest(selectedRequest.id, email);
            
            // Close both dialogs
            setDeleteDialogOpen(false);
            setDetailsOpen(false);
            
            // Show success message
            showSnackbar(`Request ${selectedRequest.REQUEST_ID} has been deleted successfully`, 'success');
            
            // Refresh the requests list
            fetchRequests();
        } catch (error) {
            console.error('Error deleting request:', error);
            
            // Handle specific error messages from the backend
            let errorMessage = 'Failed to delete request. Please try again.';
            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            }
            
            showSnackbar(errorMessage, 'error');
        } finally {
            setDeleteLoading(false);
        }
    }, [selectedRequest, email, showSnackbar, fetchRequests]);
    
    // Open delete confirmation dialog
    const openDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(true);
    }, []);

    // Add a dialog for delete confirmation
    const renderDeleteConfirmationDialog = () => (
        <mui.Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    p: 2
                }
            }}
        >
            <mui.DialogTitle sx={{ pb: 1 }}>
                Delete Request
            </mui.DialogTitle>
            
            <mui.DialogContent>
                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'warning.main' }}>
                    <WarningIcon sx={{ mr: 1 }} />
                    <mui.Typography variant="subtitle1" fontWeight={500}>
                        Are you sure you want to delete this request?
                    </mui.Typography>
                </mui.Box>
                
                <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This action cannot be undone. The request will be permanently removed from the system.
                </mui.Typography>
                
                {selectedRequest && (
                    <mui.Box
                        sx={{
                            p: 2,
                            bgcolor: alpha('#f8f9fa', 0.7),
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            mb: 2
                        }}
                    >
                        <mui.Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Request Details
                        </mui.Typography>
                        <mui.Typography variant="body2">
                            <strong>Request ID:</strong> {selectedRequest.REQUEST_ID}
                        </mui.Typography>
                        <mui.Typography variant="body2">
                            <strong>System:</strong> {selectedRequest.SYSTEM_NAME || '-'}
                        </mui.Typography>
                        <mui.Typography variant="body2">
                            <strong>Roles:</strong> {selectedRequest.ROLES || '-'}
                        </mui.Typography>
                        <mui.Typography variant="body2">
                            <strong>Status:</strong> {selectedRequest.STATUS}
                        </mui.Typography>
                    </mui.Box>
                )}
            </mui.DialogContent>
            
            <mui.DialogActions>
                <mui.Button 
                    onClick={() => setDeleteDialogOpen(false)}
                    disabled={deleteLoading}
                >
                    Cancel
                </mui.Button>
                
                <mui.Button 
                    variant="contained" 
                    color="error"
                    disabled={deleteLoading}
                    onClick={handleDeleteRequest}
                    startIcon={deleteLoading ? <mui.CircularProgress size={20} /> : null}
                >
                    Delete
                </mui.Button>
            </mui.DialogActions>
        </mui.Dialog>
    );

    // Add new function to handle review approval/rejection
    const handleReviewAction = useCallback(async (action) => {
        if (!selectedReview || !email) return;
        
        setReviewLoading(true);
        
        try {
            const reviewData = {
                id: selectedReview.id,
                STATUS: action,
                REVIEWED_BY: email,
                REVIEW_COMPLETED_ON: new Date().toISOString(),
                COMMENTS: reviewComments
            };
            
            await UARService.updateUARSODRecord(selectedReview.id, reviewData);
            
            // Close dialog and refresh reviews
            setReviewDetailsOpen(false);
            setReviewComments('');
            showSnackbar(`Access review ${action.toLowerCase()} successfully`, 'success');
            fetchAccessReviews();
        } catch (error) {
            console.error('Error updating review:', error);
            showSnackbar('Failed to update review', 'error');
        } finally {
            setReviewLoading(false);
        }
    }, [selectedReview, email, reviewComments, showSnackbar, fetchAccessReviews]);

    // Add new function to handle review selection
    const handleReviewSelect = useCallback((review) => {
        setSelectedReview(review);
        setReviewComments('');
        setReviewDetailsOpen(true);
    }, []);

    return (
        <ResponsiveContainer maxWidth="100%" padding="0">
            {/* Professional header */}
            <mui.Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    bgcolor: '#0D6EFD',
                    color: 'white',
                    p: 1.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    mb: 2,
                    width: '100%'
                }}
            >
                <mui.Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    pl: { xs: 1, sm: 2 }
                }}>
                    <img src={logo} width="35" height="35" alt="Audit-AI Logo" style={{ marginRight: '12px' }} />
                    <mui.Typography variant="h6" sx={{ 
                        fontSize: { xs: '20px', sm: '25px' },
                        fontWeight: 600
                    }}>
                        Audit-AI
                    </mui.Typography>
                </mui.Box>
            </mui.Box>
            
            {/* Main content with balanced spacing */}
            <mui.Box 
                sx={{ 
                    width: '100%',
                    maxWidth: '1400px',
                    mx: 'auto',
                    px: { xs: 2, sm: 3, md: 4, lg: 5 }
                }}
            >
                {renderContent()}
            </mui.Box>
            
            {/* Snackbar for notifications */}
            <mui.Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <mui.Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    elevation={6}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </mui.Alert>
            </mui.Snackbar>
        </ResponsiveContainer>
    );
};

export default AccessRequestDashboard; 